import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api from "../lib/api";
import { setToken, setUser } from "../lib/auth";
import { toast } from "sonner";
import { CheckCircle2, ChevronDown, ChevronUp, Receipt } from "lucide-react";

const Signup = () => {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const nav = useNavigate();
  const [form, setForm] = useState({
    business_name: "",
    email: "",
    password: "",
    siret: "",
    billing_address_line: "",
    billing_postcode: "",
    billing_city: "",
  });
  const [showBilling, setShowBilling] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!sessionId) {
      toast.error("Session de paiement manquante");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Mot de passe trop court (8 caractères minimum)");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/auth/signup", { ...form, session_id: sessionId });
      setToken(res.data.token);
      setUser(res.data.user);
      toast.success("Bienvenue ! Votre espace client est prêt.");
      nav("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la création du compte");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-[#1E2A5E] grid grid-cols-1 md:grid-cols-2 marketing-mobile-shrink" style={{ fontFamily: "'Manrope', ui-sans-serif, system-ui, sans-serif" }}>
      <div className="hidden md:block bg-gradient-to-br from-[#1E2A5E] to-[#2FA398] text-white p-12 lg:p-16 relative">
        <div className="inline-flex items-center rounded-2xl bg-white px-5 py-3 shadow-lg">
          <img
            src="/assets/logo.png"
            alt="Le Guide des Entrepreneurs"
            className="h-12 w-auto"
          />
        </div>
        <div className="mt-20 max-w-md">
          <CheckCircle2 className="w-10 h-10 text-white mb-6" />
          <h2 className="font-extrabold text-4xl lg:text-5xl tracking-tight leading-[1.05]">
            Paiement confirmé.
            <br />Créez votre espace.
          </h2>
          <p className="text-white/80 mt-6 leading-relaxed">
            Vous êtes à 30 secondes d'avoir votre page en ligne. Définissez vos identifiants et le nom de votre entreprise.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16 bg-white text-[#1E2A5E]">
        <form onSubmit={submit} className="w-full max-w-md text-[#1E2A5E]" data-testid="signup-form">
          <div className="text-xs font-bold uppercase tracking-widest text-[#2FA398] mb-4">· Création de compte</div>
          <h1 className="font-extrabold text-4xl tracking-tight mb-8 text-[#1E2A5E]">Votre espace client</h1>

          <div className="space-y-5">
            <div>
              <Label className="text-sm font-semibold mb-1.5 block text-[#1E2A5E]">Nom de l'entreprise</Label>
              <Input
                value={form.business_name}
                onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                placeholder="Atelier Lumière"
                required
                className="rounded-md border-slate-300 text-[#1E2A5E] placeholder:text-slate-400"
                data-testid="signup-business"
              />
              <p className="text-xs text-slate-500 mt-1.5">Ce nom servira pour votre URL : /entreprise/votre-entreprise</p>
            </div>
            <div>
              <Label className="text-sm font-semibold mb-1.5 block text-[#1E2A5E]">Email professionnel</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="contact@votre-entreprise.fr"
                required
                className="rounded-md border-slate-300 text-[#1E2A5E] placeholder:text-slate-400"
                data-testid="signup-email"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold mb-1.5 block text-[#1E2A5E]">Mot de passe</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Min. 8 caractères"
                required
                minLength={8}
                className="rounded-md border-slate-300 text-[#1E2A5E] placeholder:text-slate-400"
                data-testid="signup-password"
              />
            </div>

            {/* Billing details (optional, for invoice) */}
            <div className="border-t border-slate-200 pt-5">
              <button
                type="button"
                onClick={() => setShowBilling((v) => !v)}
                className="flex items-center gap-2 text-sm font-semibold text-[#1E2A5E] hover:text-[#2FA398]"
                data-testid="signup-toggle-billing"
              >
                <Receipt className="w-4 h-4" />
                Détails de facturation (optionnel)
                {showBilling ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              <p className="text-xs text-slate-500 mt-1.5">
                Si vous êtes une entreprise (SIRET), renseignez ces informations pour qu'elles apparaissent sur votre facture Factur-X. Vous pourrez les modifier plus tard.
              </p>
              {showBilling && (
                <div className="mt-4 space-y-4 bg-slate-50 p-4 border border-slate-200 rounded-md text-[#1E2A5E]" data-testid="signup-billing-block">
                  <div>
                    <Label className="text-xs font-semibold mb-1.5 block uppercase tracking-wider text-slate-600">Numéro SIRET</Label>
                    <Input
                      value={form.siret}
                      onChange={(e) => setForm({ ...form, siret: e.target.value })}
                      placeholder="123 456 789 00012"
                      className="rounded-md border-slate-300 bg-white text-[#1E2A5E] placeholder:text-slate-400"
                      data-testid="signup-siret"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold mb-1.5 block uppercase tracking-wider text-slate-600">Adresse de facturation</Label>
                    <Input
                      value={form.billing_address_line}
                      onChange={(e) => setForm({ ...form, billing_address_line: e.target.value })}
                      placeholder="12 rue de la République"
                      className="rounded-md border-slate-300 bg-white text-[#1E2A5E] placeholder:text-slate-400"
                      data-testid="signup-billing-address"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-1">
                      <Label className="text-xs font-semibold mb-1.5 block uppercase tracking-wider text-slate-600">Code postal</Label>
                      <Input
                        value={form.billing_postcode}
                        onChange={(e) => setForm({ ...form, billing_postcode: e.target.value })}
                        placeholder="75001"
                        className="rounded-md border-slate-300 bg-white text-[#1E2A5E] placeholder:text-slate-400"
                        data-testid="signup-billing-postcode"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs font-semibold mb-1.5 block uppercase tracking-wider text-slate-600">Ville</Label>
                      <Input
                        value={form.billing_city}
                        onChange={(e) => setForm({ ...form, billing_city: e.target.value })}
                        placeholder="Paris"
                        className="rounded-md border-slate-300 bg-white text-[#1E2A5E] placeholder:text-slate-400"
                        data-testid="signup-billing-city"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full mt-8 rounded-full bg-[#1E2A5E] hover:bg-[#2FA398] text-white py-6 text-base font-bold"
            data-testid="signup-submit"
          >
            {loading ? "Création…" : "Créer mon compte"}
          </Button>

          <p className="text-sm text-slate-500 mt-6 text-center">
            Déjà un compte ? <Link to="/mon-compte-client" className="text-[#1E2A5E] font-semibold hover:text-[#2FA398]">Se connecter</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default Signup;
